import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAppSelector } from '../store/hooks';
import LoadingSpinner from './LoadingSpinner';
import { formatDateEuropean } from '../utils/dateUtils';
import './Students.css';
import './GroupForm.css';
import './Giveaways.css';
import './RewardSubscriptionBox.css';

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

type GiveawayRow = {
  id: number;
  name: string;
  startsOn: string;
  endsOn: string;
  mentionUntil: string;
  status: 'upcoming' | 'open' | 'grace' | 'closed';
  mentionCount: number;
  groups: { id: number; name: string | null; level: string }[];
};

function groupLabel(group: GiveawayRow['groups'][number]) {
  return group.name?.trim() || `${group.level.toUpperCase()} Group`;
}

function statusLabel(status: GiveawayRow['status']) {
  if (status === 'upcoming') return 'Upcoming';
  if (status === 'open') return 'Open';
  if (status === 'grace') return 'Grace';
  return 'Closed';
}

const Giveaways: React.FC = () => {
  const token = useAppSelector((state) => state.auth.token);
  const navigate = useNavigate();
  const [giveaways, setGiveaways] = useState<GiveawayRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    itemsPerPage: 50,
  });
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API_BASE_URL}/giveaways`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { page, limit: 50 },
    }).then((response) => {
      setGiveaways(response.data.giveaways || []);
      setPagination(response.data.pagination);
    }).catch((err) => {
      setError(axios.isAxiosError(err) ? err.response?.data?.error || err.message : 'Failed to load giveaways');
    }).finally(() => setLoading(false));
  }, [page, token]);

  return (
    <div className="students-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Giveaways</h1>
          <p>Short mention experiments in selected groups</p>
        </div>
        <button type="button" className="btn-primary" onClick={() => navigate('/giveaways/create')}>
          + New Giveaway
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <LoadingSpinner />
      ) : giveaways.length === 0 ? (
        <div className="no-students"><p>No giveaways yet. Create one to open mention rewards for selected groups.</p></div>
      ) : (
        <>
          <div className="results-summary">
            <p>Showing {giveaways.length} of {pagination.totalItems} giveaways</p>
          </div>
          <div className="students-table-container">
            <table className="students-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Period</th>
                  <th>Mentions until</th>
                  <th>Groups</th>
                  <th>Mentions</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {giveaways.map((giveaway) => (
                  <tr key={giveaway.id}>
                    <td>
                      <button
                        type="button"
                        className="table-link-button"
                        onClick={() => navigate(`/giveaways/edit/${giveaway.id}`)}
                      >
                        {giveaway.name}
                      </button>
                    </td>
                    <td className="mention-period">
                      {formatDateEuropean(giveaway.startsOn)}
                      {' – '}
                      {formatDateEuropean(giveaway.endsOn)}
                    </td>
                    <td>{formatDateEuropean(giveaway.mentionUntil)}</td>
                    <td>
                      <div className="giveaway-group-chips">
                        {giveaway.groups.map((group) => (
                          <span key={group.id} className="giveaway-group-chip">{groupLabel(group)}</span>
                        ))}
                      </div>
                    </td>
                    <td>{giveaway.mentionCount}</td>
                    <td>
                      <span className={`mention-status mention-status--${giveaway.status}`}>
                        {statusLabel(giveaway.status)}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn-icon"
                        data-tooltip="Edit"
                        aria-label="Edit giveaway"
                        onClick={() => navigate(`/giveaways/edit/${giveaway.id}`)}
                      >
                        ✎
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {pagination.totalPages > 1 && (
        <div className="pagination">
          <button type="button" className="btn-secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
          <span>Page {pagination.currentPage} of {pagination.totalPages}</span>
          <button type="button" className="btn-secondary" disabled={page >= pagination.totalPages} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      )}
    </div>
  );
};

export default Giveaways;
